import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";
import { useLanguage } from "../i18n/LanguageProvider";

export default function ContactPage({
  showBackButton = false,
  onBack = null,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { language } = useLanguage();

  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [alertText, setAlertText] = useState("");
  const alert = (text) => setAlertText(String(text || "").trim());

  const handleBack = () => {
    if (typeof onBack === "function") {
      onBack();
      return;
    }

    if (location.state?.from === "landing") {
      navigate("/landing");
      return;
    }

    navigate("/");
  };

  const handleSubmit = async () => {
    if (!email.trim()) {
      alert(
        language === "de"
          ? "Bitte E-Mail eingeben."
          : "Please enter an email address."
      );
      return;
    }

    if (!subject.trim()) {
      alert(
        language === "de"
          ? "Bitte Betreff eingeben."
          : "Please enter a subject."
      );
      return;
    }

    if (!message.trim()) {
      alert(
        language === "de"
          ? "Bitte Nachricht eingeben."
          : "Please enter a message."
      );
      return;
    }

    try {
      setLoading(true);

      const submitLandingContactForm = httpsCallable(
        functions,
        "submitLandingContactForm"
      );

      await submitLandingContactForm({
        email,
        subject,
        message,
        language,
      });

      setSuccess(true);

      setEmail("");
      setSubject("");
      setMessage("");
    } catch (err) {
      console.error("Contact request failed:", err?.code || err?.message || "unknown");

      alert(
        language === "de"
          ? "Die Anfrage konnte nicht gesendet werden."
          : "The request could not be sent."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        width: "92%",
        margin: "0 auto",
        padding: "calc(18px + var(--vq-safe-area-top)) calc(16px + var(--vq-safe-area-right)) calc(28px + var(--vq-safe-area-bottom)) calc(16px + var(--vq-safe-area-left))",
        borderRadius: "16px",
        background: "rgba(255,255,255,0.04)",
        border: "1px solid rgba(255,255,255,0.10)",
        color: "white",
        boxSizing: "border-box",
      }}
    >
      {showBackButton && (
        <button
          onClick={handleBack}
          style={{
            background: "transparent",
            border: "1px solid rgba(255,255,255,0.2)",
            color: "#fff",
            padding: "10px 16px",
            borderRadius: "10px",
            cursor: "pointer",
            marginBottom: "30px",
            fontSize: "15px",
          }}
        >
          ← {language === "de" ? "Zurück" : "Back"}
        </button>
      )}

      <h1
        style={{
          marginTop: 0,
          fontSize: "clamp(22px, 5vw, 28px)",
          fontWeight: "800",
          marginBottom: "24px",
          lineHeight: 1.2,
        }}
      >
        {language === "de" ? "Kontakt" : "Contact"}
      </h1>

      {success && (
        <div
          style={{
            marginBottom: "20px",
            padding: "14px",
            borderRadius: "10px",
            background: "rgba(0,200,120,0.15)",
            border: "1px solid rgba(0,200,120,0.4)",
          }}
        >
          {language === "de"
            ? "Deine Anfrage wurde erfolgreich versendet."
            : "Your request has been sent successfully."}
        </div>
      )}

      <div style={{ marginBottom: "16px" }}>
        <label>E-Mail</label>

        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          style={styles.input}
        />
      </div>

      <div style={{ marginBottom: "16px" }}>
        <label>
          {language === "de" ? "Betreff" : "Subject"}
        </label>

        <input
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          style={styles.input}
        />
      </div>

      <div style={{ marginBottom: "24px" }}>
        <label>
          {language === "de" ? "Nachricht" : "Message"}
        </label>

        <textarea
          rows={8}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          style={styles.textarea}
        />
      </div>

      <button
        onClick={handleSubmit}
        disabled={loading}
        style={styles.button}
      >
        {loading
          ? language === "de"
            ? "Wird gesendet..."
            : "Sending..."
          : language === "de"
          ? "Anfrage senden"
          : "Send request"}
      </button>

      {alertText && (
        <div
          onClick={() => setAlertText("")}
          style={styles.alertOverlay}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={styles.alertBox}
          >
            <div style={styles.alertTitle}>VaultQuest</div>
            <div style={styles.alertBody}>{alertText}</div>
            <button
              type="button"
              onClick={() => setAlertText("")}
              style={styles.alertButton}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  input: {
    width: "100%",
    marginTop: "8px",
    padding: "12px",
    borderRadius: "10px",
    border: "1px solid rgba(255,255,255,0.15)",
    background: "rgba(255,255,255,0.05)",
    color: "#fff",
    boxSizing: "border-box",
  },

  textarea: {
    width: "100%",
    marginTop: "8px",
    padding: "12px",
    borderRadius: "10px",
    border: "1px solid rgba(255,255,255,0.15)",
    background: "rgba(255,255,255,0.05)",
    color: "#fff",
    boxSizing: "border-box",
    resize: "vertical",
  },

  button: {
    background: "#7a2bff",
    color: "#fff",
    border: "none",
    padding: "14px 20px",
    borderRadius: "10px",
    fontWeight: "700",
    cursor: "pointer",
  },

  alertOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.82)",
    zIndex: 1000000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
  },

  alertBox: {
    width: "100%",
    maxWidth: 360,
    borderRadius: 18,
    border: "2px solid rgba(255,255,255,0.35)",
    background: "linear-gradient(135deg, #ff007f 0%, #7a2bff 100%)",
    padding: 18,
    color: "white",
    textAlign: "center",
    fontFamily: "'Cooper Hewitt', system-ui",
  },

  alertTitle: {
    fontSize: 18,
    fontWeight: 1000,
    textTransform: "uppercase",
    marginBottom: 10,
  },

  alertBody: {
    fontSize: 13,
    fontWeight: 900,
    lineHeight: 1.45,
  },

  alertButton: {
    marginTop: 16,
    width: "100%",
    height: 50,
    borderRadius: 14,
    border: "2px solid rgba(255,255,255,0.55)",
    background: "white",
    color: "black",
    fontWeight: 1000,
    cursor: "pointer",
    textTransform: "uppercase",
  },
};
